/**
 * システム状態の自己診断の画面（F-19）。
 *
 * ★**判断を DOM から切り離す。**「何もなければ0件」を必ず言う（`closing-view.ts` と同じ約束。
 *   `.loop/memory.md`「走った件数と飛ばした件数を分けて聞く」の親戚——
 *   ここでは「見た項目と、見ていない項目（バックアップ）」を混ぜない）。
 */

import type { ExpiringParameter, SelfDiagnosisResult } from "./api.js";

/** 4項目とも問題なしか（画面が「問題なし」を出す条件） */
export function hasNoIssues(data: SelfDiagnosisResult): boolean {
  return (
    data.expiringParameters.length === 0 &&
    data.pendingDocuments.count === 0 &&
    data.unclosedMonths.length === 0 &&
    !data.kintaiIngest.warning &&
    data.kintaiIngest.heldSessionCount === 0
  );
}

/** kintai 受け口の状態の説明文（KINTAI-IMPORT AC-13）。無効なら null（出す理由が無い） */
export function describeKintaiIngest(data: SelfDiagnosisResult["kintaiIngest"]): string | null {
  if (!data.enabled) return null;
  const parts: string[] = [];
  if (data.warning) {
    parts.push(
      data.lastReceivedAt === null
        ? "kintaiからの受信がまだありません"
        : `kintaiからの最終受信は ${data.lastReceivedAt}（しばらく届いていません）`,
    );
  }
  if (data.heldSessionCount > 0) {
    parts.push(`保留中の取込セッションが ${String(data.heldSessionCount)}件あります`);
  }
  return parts.length === 0 ? null : parts.join("。");
}

/**
 * 保留（HELD）1行ぶんの説明文と、次の行動（B3・spec）。
 *
 * ★氏名・時給・分数は引数にも文言にも出さない。理由コード・年月・件数だけから組み立てる。
 */
export function describeKintaiHeldReason(item: {
  readonly reasonCode: string;
  readonly yearMonth: string | null;
  readonly count: number;
}): string {
  const n = String(item.count);
  const ym = item.yearMonth ?? "対象年月不明";
  switch (item.reasonCode) {
    case "MONTH_CLOSED":
      return `${ym} が締め済みのため${n}件保留中。締めを解除すると次回の送信で反映されます`;
    case "MONTH_SUBMITTED":
      return `${ym} が申請中のため${n}件保留中。確定または差し戻し後、次回の送信で反映されます`;
    case "CONFLICT_PUNCH_OR_MANUAL":
      return `本体側の打刻と重複しているため${n}件保留中。店長が本体側の打刻をご確認ください。重複が解消すると次回の送信で反映されます`;
    case "NIGHT_WINDOW_NOT_CONFIGURED":
      return `深夜時間帯が未設定のため${n}件保留中。オーナーがパラメータを設定してください`;
    case "NAME_MISMATCH":
      // ★2026-09-22 に廃止した理由コード（NAME-FROM-KINTAI）。それ以前に保留された分だけが残りうる
      return `以前の氏名の照合で${n}件保留中（この照合は廃止済み）。kintai から同じ勤務を送り直すと反映されます`;
    default:
      return `${item.reasonCode} のため${n}件保留中`;
  }
}

/**
 * 1件の説明文。★**「切れた」と「切れかけ」を混ぜない**（`closing-view.ts` の
 *   `failed` と `skipped` を混ぜない、と同じ理屈——直す緊急度が違う）。
 */
export function describeExpiry(p: ExpiringParameter): string {
  if (p.daysRemaining <= 0) {
    return `期限切れです（${String(Math.abs(p.daysRemaining))}日前・${p.validTo}まで）`;
  }
  return `あと${String(p.daysRemaining)}日で切れます（${p.validTo}まで）`;
}
